import classNames from "classnames";
import {
	FormEvent,
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useRef
} from "react";
import {ControlAttachmentContext} from "../ControlAttachment";
import {FormItem} from "../FormItem/FormItem";
import {
	SearchInput,
	SearchInputHandle,
	SearchInputProps
} from "../SearchInput/SearchInput";
import styles from "./SearchInputForm.module.less";

export type SearchInputFormProps = {
	hasMarginBottom?: boolean;
	addon?: ReactElement;
} & SearchInputProps;

export type SearchInputFormHandle = {
	focusInput(): void;
	blurInput(): void;
};

export const SearchInputForm = forwardRef(
	(
		props: SearchInputFormProps,
		ref: Ref<SearchInputFormHandle>
	): ReactElement => {
		const {
			hasMarginBottom = false,
			addon,
			height = "auto",
			...searchInputProps
		} = props;

		const inputRef: RefObject<SearchInputHandle | null> = useRef<SearchInputHandle>(null);

		useImperativeHandle(
			ref,
			() => ({
				focusInput: () => inputRef.current
					?.focus(),
				blurInput: () => inputRef.current
					?.blur()
			}),
			[]
		);

		const submitHandler = useCallback(
			(args: FormEvent<HTMLFormElement>): void => {
				args.preventDefault();
				args.stopPropagation();
			},
			[]
		);

		return (
			<form
				className={classNames(
					styles.root,
					{
						[styles.root_withoutMargin]: !hasMarginBottom,
						[styles.root_fillHeight]: height === "fill",
						[styles.root_largeHeight]: height === "large",
					}
				)}
				onSubmit={submitHandler}
			>
				<FormItem
					height={height === "fill" ? "stretch" : "auto"}
					hasNoMargin={!hasMarginBottom}
				>
					<div className={styles.row}>
						<div className={styles.input}>
							<SearchInput
								{...searchInputProps}
								attachedOn={addon ? "right" : undefined}
								ref={inputRef}
								height={height}
							/>
						</div>
						{addon && (
							<div className={styles.addon}>
								<ControlAttachmentContext.Provider value={{attachedOn: "left"}}>
									{addon}
								</ControlAttachmentContext.Provider>
							</div>
						)}
					</div>
				</FormItem>
			</form>
		);
	}
);

SearchInputForm.displayName = "SearchInputForm";
